import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import {
  AdditionalIDPhotosEndpoints,
  AdditionalIDPhotosReviewsEndpoints,
  IDPhotosEndpoints,
  IDPhotosReviewsEndpoints,
} from '@pims-endpoints';
import { rxResource } from '@angular/core/rxjs-interop';
import { computed } from '@angular/core';
import { PhotosConstants, VisitDetailPhotos } from '@types-lib';
import { catchError, EMPTY, finalize, Subject, switchMap, tap } from 'rxjs';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { withResourcePolling } from '@shared-utils';
import { isEqualWith } from 'lodash';

const PHOTO_STATUSES = {
  1: 'Previously requested photos are yet to be uploaded.',
  2: 'You cannot request until previous photos have been approved.',
};

interface PhotoStoreState {
  photos: VisitDetailPhotos.Photo[];
  additionalPhotos: VisitDetailPhotos.Photo[];
  visitId: number;
  isUpdating: boolean;
  updateError: string | null;
}

const initialState: PhotoStoreState = {
  photos: [],
  additionalPhotos: [],
  visitId: 0,
  isUpdating: false,
  updateError: null as string | null,
};

const filterDuplicatePhotos = (photos: VisitDetailPhotos.Photo[]) => {
  const seen = new Set();
  const filteredPhotos = photos.filter(photo => {
    const duplicate = seen.has(photo.file?.id);
    seen.add(photo.file?.id);
    return !duplicate && photo.file;
  });
  return filteredPhotos;
};

const processPhotos = (photos: VisitDetailPhotos.Photo[]) => {
  const processedPhotos = photos.map((photo: VisitDetailPhotos.Photo) => ({
    ...photo,
    rejected: photo.file && photo.file.status === PhotosConstants.PhotoStatuses.Rejected,
    deleted: photo.file && photo.file.deleted,
    approved: photo.file && photo.file.status === PhotosConstants.PhotoStatuses.Approved,
  }));
  return filterDuplicatePhotos(processedPhotos);
};

export const IdPhotosStore = signalStore(
  withState(initialState),
  withResourcePolling({
    intervalMs: 10000,
  }),
  withProps(() => ({
    _idPhotosEndpoint: new IDPhotosEndpoints(),
    _additionalIdPhotosEndpoint: new AdditionalIDPhotosEndpoints(),
    _idPhotosReviewsEndpoint: new IDPhotosReviewsEndpoints(),
    _additionalIdPhotosReviewsEndpoint: new AdditionalIDPhotosReviewsEndpoints(),
    _photoTaskSuccess$: new Subject<{ approved: boolean; additional: boolean }>(),
    _requestAdditionalPhotoSuccess$: new Subject(),
    _photoTaskError$: new Subject<{ error: any; approved: boolean; additional: boolean }>(),
    _requestAdditionalPhotoError$: new Subject<{ error: any }>(),
  })),
  withProps(store => {
    const _photosResource = rxResource({
      params: () => store.visitId(),
      stream: ({ params }) => {
        return store._idPhotosEndpoint.get(params);
      },
      equal: (previous, current) => {
        // Check if data has changed to prevent all computation if it did not
        if (!previous?.photos || !current?.photos) return previous === current;
        return isEqualWith(previous.photos, current.photos, (a, b, key) => {
          if (key === 'id') {
            return true;
          }
          return undefined;
        });
      },
    });
    const _additionalPhotosResource = rxResource({
      params: () => store.visitId(),
      stream: ({ params }) => {
        return store._additionalIdPhotosEndpoint.get(params);
      },
      equal: (previous, current) => {
        // Check if data has changed to prevent all computation if it did not
        if (!previous?.photos || !current?.photos) return previous === current;
        return isEqualWith(previous.photos, current.photos, (a, b, key) => {
          if (key === 'id') {
            return true;
          }
          return undefined;
        });
      },
    });
    return {
      photosResource: _photosResource,
      additionalPhotosResource: _additionalPhotosResource,
    };
  }),
  withMethods(store => ({
    setVisitId: (visitId: number) => {
      patchState(store, { visitId });
    },
    updatePhotoTask: rxMethod<{
      payload: VisitDetailPhotos.UpdatePhotoPayload;
      approved: boolean;
      additional: boolean;
    }>(
      switchMap(({ payload, approved, additional }) => {
        const visitId = store.visitId();
        if (!visitId) {
          console.error('No visitId set');
          return EMPTY;
        }
        patchState(store, { isUpdating: true, updateError: null });
        const endpoint = additional ? store._additionalIdPhotosReviewsEndpoint : store._idPhotosReviewsEndpoint;

        return endpoint.post(visitId, payload).pipe(
          tap(() => {
            console.log('Photo task updated successfully');
            store._photoTaskSuccess$.next({ approved, additional });
            if (additional) {
              store.additionalPhotosResource.reload();
            } else {
              store.photosResource.reload();
            }
          }),
          catchError((error: any) => {
            console.log('Error updating photo task:', error);
            store._photoTaskError$.next({ error, approved, additional });
            patchState(store, {
              updateError: error?.message || 'Failed to update photo task',
            });
            return EMPTY;
          }),
          finalize(() => patchState(store, { isUpdating: false })),
        );
      }),
    ),

    requestAdditionalId: rxMethod<{ payload: VisitDetailPhotos.RequestAdditionalIdMessage }>(
      switchMap(({ payload }) => {
        const visitId = store.visitId();
        if (!visitId) {
          console.error('No visitId set');
          return EMPTY;
        }
        patchState(store, { isUpdating: true, updateError: null });

        return store._additionalIdPhotosEndpoint.post(visitId, payload).pipe(
          tap(() => {
            store._requestAdditionalPhotoSuccess$.next({});
          }),
          catchError(error => {
            console.log('Error requesting additional ID photo.');
            store._requestAdditionalPhotoError$.next({ error });
            return EMPTY;
          }),
          finalize(() => patchState(store, { isUpdating: false })),
        );
      }),
    ),

    clearUpdateError: () => {
      patchState(store, { updateError: null });
    },

    refreshPhotos: () => {
      store.photosResource.reload();
      store.additionalPhotosResource.reload();
    },
    onPhotoTaskSuccess: () => store._photoTaskSuccess$.asObservable(),
    onPhotoTaskError: () => store._photoTaskError$.asObservable(),
    onRequestAdditionalPhotoSuccess: () => store._requestAdditionalPhotoSuccess$.asObservable(),
    onRequestAdditionalPhotoError: () => store._requestAdditionalPhotoError$.asObservable(),
  })),
  withComputed(store => ({
    processedPhotos: computed(() => {
      const photos = store.photosResource.value()?.photos || [];
      return processPhotos(photos);
    }),

    hasUnapprovedPhotos: computed(() => {
      const photos = store.photosResource.value()?.photos || [];
      return !!(
        processPhotos(photos).length && !processPhotos(photos).every((photo: VisitDetailPhotos.Photo) => photo.approved)
      );
    }),

    hasUnapprovedAdditionalPhotos: computed(() => {
      const photos = store.additionalPhotosResource.value()?.photos || [];
      return !!(
        processPhotos(photos).length && !processPhotos(photos).every((photo: VisitDetailPhotos.Photo) => photo.approved)
      );
    }),

    processedAdditionalPhotos: computed(() => {
      const photos = store.additionalPhotosResource.value()?.photos || [];
      return processPhotos(photos);
    }),

    isLoading: computed(
      () => store.photosResource.isLoading() || store.additionalPhotosResource.isLoading() || store.isUpdating(),
    ),

    hasError: computed(
      () => !!store.photosResource.error() || !!store.additionalPhotosResource.error() || !!store.updateError(),
    ),

    idPhotoError: computed(() => store.photosResource.error()),

    additionalIdPhotoError: computed(() => store.additionalPhotosResource.error()),

    diagnosis: computed(() => store.photosResource.value()?.diagnosis || ''),

    additionalDiagnosis: computed(() => store.additionalPhotosResource.value()?.diagnosis || ''),

    requestCounts: computed(() => store.additionalPhotosResource.value()?.count),

    photoTypes: computed(() => store.photosResource.value()?.id_types || []),

    count: computed(() => store.additionalPhotosResource.value()?.count),

    canRequest: computed(() => {
      const count = store.additionalPhotosResource.value()?.count;
      const additionalPhotos = processPhotos(store.additionalPhotosResource.value()?.photos || []);
      return !(count?.id_and_selfie_unapproved || additionalPhotos.length);
    }),

    cannotRequestMessage: computed(() => {
      const count = store.additionalPhotosResource.value()?.count;
      const additionalPhotos = processPhotos(store.additionalPhotosResource.value()?.photos || []);
      return count?.id_and_selfie_unapproved ? PHOTO_STATUSES[2] : additionalPhotos.length ? PHOTO_STATUSES[1] : '';
    }),
  })),
);
